import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { zonalManagersQueryOptions } from '@/features/users/api/users.api';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { getZonalManagerFilterField } from '@/utils/filterFields';
import { mpAdjustmentInfiniteQueryOptions } from '../api/marketPromotion.api';
import { MpAreaFilterPanel } from '../components/MpAreaFilterPanel';
import { getMpAdjustmentColumns } from '../components/MpColumns';
import { useMpAdjustmentFilters } from '../hooks/useMpReportFilters';

export function MpAdjustmentPage() {
  const controller = useMpAdjustmentFilters();
  const { rows, isLoading, loadError, refetch, loadMoreProps } = useInfiniteTableQuery(
    mpAdjustmentInfiniteQueryOptions(controller.params),
  );
  const { data: zonalManagers } = useQuery(zonalManagersQueryOptions());

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const lastViewedItemId = useRevealLastViewedRow('promoItems');

  const searchText = controller.params.search;
  const columns = useMemo(
    () => getMpAdjustmentColumns(controller.drillParams, searchText),
    [controller.drillParams, searchText],
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Market Promotion Adjustment"
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={controller.params.dateFrom}
            dateTo={controller.params.dateTo}
          />
        }
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: 'Project code or name',
        }}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <MpAreaFilterPanel
          filters={controller}
          personField={getZonalManagerFilterField(zonalManagers)}
        />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the report"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="name"
          isRowHighlighted={(item) => item.promoItemId === lastViewedItemId}
          emptyMessage="No cash promotion went out in this period"
          {...loadMoreProps}
        />
      )}
    </div>
  );
}
